import React from 'react';
import './Home.css';
import bundledBookCover from '../../assets/mother-wound-cover.jpeg';
import { useContent } from '../../context/ContentContext';

/*
 * The endorsements under the title, all of them written in Airtable.
 *
 * The first is the row keyed home_blurb; any after it are home_blurb_2,
 * home_blurb_3 and so on, each beside a row of the same name ending _author.
 * Reading them by that pattern rather than from a fixed list means a fourth can
 * be added in the base alone, and it costs no extra call: these rows travel
 * with the Site Content the page already asks for. A row left blank is passed
 * over, so an empty one waiting to be written shows nothing.
 */
const NUMBERED_BLURB = /^home_blurb_(\d+)$/;

const blurbsFrom = (siteContent) => {
  const numbered = Object.keys(siteContent)
    .map((key) => NUMBERED_BLURB.exec(key))
    .filter(Boolean)
    .sort((a, b) => Number(a[1]) - Number(b[1]))
    .map((match) => match[0]);

  return ['home_blurb', ...numbered]
    .filter((key) => siteContent[key])
    .map((key) => ({
      text: siteContent[key],
      author: siteContent[`${key}_author`] || '',
    }));
};

const Home = () => {
  const { siteContent, siteImages } = useContent();

  const blurbs = blurbsFrom(siteContent);
  return (
    <div className="home">
      <div className="home-grid">
        <div className="home-cover">
          <img
            className="home-cover-image"
            src={siteImages.home_book_image || bundledBookCover}
            alt={`${siteContent.home_book_title} book cover`}
          />
        </div>
        <div className="home-details">
          {/*
            * The title and the endorsement are grouped apart from the prose so
            * that on a phone they can sit above the cover while the rest of the
            * column stays below it.
            */}
          <div className="home-heading">
            {siteContent.home_eyebrow && (
              <p className="home-eyebrow">{siteContent.home_eyebrow}</p>
            )}
            <h1 className="home-title">{siteContent.home_book_title}</h1>
            {siteContent.home_book_subtitle && (
              <p className="home-subtitle">{siteContent.home_book_subtitle}</p>
            )}
            {blurbs.length > 0 && (
              <div className={blurbs.length > 1 ? 'home-blurbs home-blurbs-stacked' : 'home-blurbs'}>
                {blurbs.map((blurb) => (
                  <blockquote className="home-blurb" key={blurb.text}>
                    <p className="home-blurb-text">
                      &ldquo;
                      {blurb.text}
                      &rdquo;
                    </p>
                    {blurb.author && (
                      <footer className="home-blurb-author">{blurb.author}</footer>
                    )}
                  </blockquote>
                ))}
              </div>
            )}
          </div>
          <div className="home-body">
            <span className="home-divider" aria-hidden="true" />
            <p className="home-description">{siteContent.home_book_description}</p>
            {siteContent.home_on_sale && (
              <p className="home-meta">{siteContent.home_on_sale}</p>
            )}
            {siteContent.home_preorder_url && (
              <a
                className="home-preorder-button"
                href={siteContent.home_preorder_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {siteContent.home_preorder_label}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
